import { useForm } from "react-hook-form"
import { useQueryClient } from "@tanstack/react-query"
import { Loader2 } from "lucide-react"
import { toast } from "sonner"
import { accountKeys } from "@apis/accounts/queryKeys"
import type { IUpdateAccountNumberRequest } from "@apis/accounts/types"
import {
  useAccount,
  useUpdateAccountNumberMutation,
} from "@apis/accounts/useAccounts"
import { Button } from "@components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/ui/form"
import { Input } from "@components/ui/input"
import { useHasPermissions } from "@hooks/useHasPermissions"

type TSetAccountNumberFormProps = {
  account_id: string
}

export const SetAccountNumberForm = ({
  account_id,
}: TSetAccountNumberFormProps) => {
  const queryClient = useQueryClient()
  const getAccount = useAccount(account_id)
  const updateAccountNumber = useUpdateAccountNumberMutation()
  const { canManuallySetAccountNumber } = useHasPermissions()

  const market = getAccount.data?.market
  const accountNumberLabel = market?.account_num_synonym || "Account Number"
  const accountNumberExample = market?.validation_rules?.account_number_example
  const accountNumberRegexp = market?.validation_rules?.account_number_regexp

  const form = useForm<IUpdateAccountNumberRequest>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: { account_number: "" },
  })

  if (!canManuallySetAccountNumber) return null

  const handleSubmit = ({ account_number }: IUpdateAccountNumberRequest) => {
    updateAccountNumber.mutate(
      { account_id, request: { account_number } },
      {
        onSuccess: () => {
          toast.success(`Successfully updated ${accountNumberLabel}!`)
          form.reset()
          queryClient.invalidateQueries({
            queryKey: accountKeys.detail(account_id),
          })
        },
        onError: (error) =>
          toast.error(`Error updating ${accountNumberLabel}! ${error}`),
      },
    )
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="tw:grid tw:grid-cols-12 tw:gap-4"
      >
        <div className="tw:col-span-12">
          <FormField
            control={form.control}
            name="account_number"
            rules={{
              required: `${accountNumberLabel} is required`,
              pattern: accountNumberRegexp
                ? {
                    value: new RegExp(accountNumberRegexp),
                    message: `Invalid ${accountNumberLabel} format. It should be ${accountNumberExample}`,
                  }
                : undefined,
            }}
            render={({ field }) => (
              <FormItem className="tw:col-span-12">
                <FormLabel className="tw:capitalize">
                  {accountNumberLabel}
                </FormLabel>
                <FormControl>
                  <Input placeholder={accountNumberExample} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:col-span-12 tw:justify-self-end">
          <Button
            type="submit"
            disabled={updateAccountNumber.isPending || getAccount.isPending}
          >
            {updateAccountNumber.isPending ? (
              <Loader2 className="tw:animate-spin" />
            ) : (
              <span>Update Account Number</span>
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
